// JOBE-0044.6 + JOBE-0044.9: SavedViewsBlock — built-in saved views at
// the top of the left rail; user-saved views append below.
//
// Click on a built-in row → write `?view=<id>` exclusively (any pre-
// existing chip params are dropped). The view's queryTemplate resolves
// at click time to a FilterChip[] which we hand to the parent's
// setChips callback; the URL writer (useLibraryUrlState) re-serializes
// from there.
//
// JOBE-0044.9 wires the "+ Save view" affordance to a modal; the
// parent owns the chip set and the create handler. The user-saved
// list renders via UserSavedViewsBlock below the built-in six.

import React, { useState } from 'react';
import { BUILT_IN_SAVED_VIEWS, resolveViewToChips } from './savedViews.js';
import { RailRow } from './RailRow.jsx';
import { UserSavedViewsBlock } from './UserSavedViewsBlock.jsx';
import { SavedViewModal } from './SavedViewModal.jsx';
import { useUserSavedViews } from './useUserSavedViews.js';

export function SavedViewsBlock({
  activeView,
  onPickView,
  // Future graph principal hookup; LibraryListPage threads
  // `principalUrn` in once an identity surface lands. v1 callers may
  // pass `null` — `mine-this-week` still resolves but skips the `by:`
  // chip.
  principalUrn = null,
  // JOBE-0044.9 — chips at save time + on-success callback so the
  // page can re-anchor `?view=<slug>` to the freshly-saved view. The
  // hook owns the BFF call.
  currentChips = [],
  onAfterSaveCurrentView,
  // JOBE-0044.9 — externally-driven create-modal trigger so the
  // OmniPalette's ⌘↩ chord can open the same modal the rail's "+
  // Save view" button opens. Parent toggles the boolean; the block
  // calls `onCreateModalClose` to clear it.
  createOpen = false,
  onCreateModalClose,
}) {
  const userViews = useUserSavedViews();
  const [creating, setCreating] = useState(false);
  // Effective "is the create modal open" — either the local state
  // (rail's "+ Save view" button) or the parent-driven flag (omni
  // palette ⌘↩). Either entry point feeds the same modal.
  const modalOpen = creating || createOpen;
  const closeModal = () => {
    setCreating(false);
    if (onCreateModalClose) onCreateModalClose();
  };

  const handlePickBuiltIn = (view) => {
    const chips = resolveViewToChips(view.id, {
      now: Date.now(),
      principalUrn,
    });
    if (onPickView) onPickView(view.id, chips);
  };

  const handlePickUserView = (row) => {
    if (!row) return;
    let parsed = [];
    try {
      const v = JSON.parse(row.query || '[]');
      if (Array.isArray(v)) parsed = v;
    } catch (_) {
      parsed = [];
    }
    if (onPickView) onPickView(row.slug, parsed);
  };

  const handleCreateSubmit = async ({ name }) => {
    const queryString = JSON.stringify(currentChips || []);
    const row = await userViews.create({ name, query: queryString });
    closeModal();
    if (onAfterSaveCurrentView && row && row.slug) {
      onAfterSaveCurrentView(row);
    }
  };

  return (
    <section className="library-rail-block" aria-label="Saved views">
      <header className="library-rail-block-header">
        <h3 className="library-rail-block-title">Saved views</h3>
        <button
          type="button"
          className="library-rail-block-action library-rail-block-save-view"
          onClick={() => setCreating(true)}
          title="Save the current filter set as a new view"
        >
          + Save view
        </button>
      </header>
      <ul className="library-rail-block-list">
        {BUILT_IN_SAVED_VIEWS.map((view) => (
          <li key={view.id}>
            <RailRow
              glyph={view.starred ? '★' : '·'}
              label={view.label}
              title={view.description}
              selected={activeView === view.id}
              onClick={() => handlePickBuiltIn(view)}
            />
          </li>
        ))}
      </ul>
      <UserSavedViewsBlock
        views={userViews.views}
        status={userViews.status}
        activeView={activeView}
        onPickView={handlePickUserView}
        onRename={userViews.rename}
        onRemove={userViews.remove}
      />
      {modalOpen ? (
        <SavedViewModal
          mode="create"
          chips={currentChips}
          onSubmit={handleCreateSubmit}
          onCancel={closeModal}
        />
      ) : null}
    </section>
  );
}
